import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip as RechartsTooltip,
} from "recharts";
import { CheckCircle2, Clock, Receipt, Activity } from "lucide-react";

interface Props {
  children: any[];
  todayAttendance: any[];
  todayLessonsCount: number;
  pendingAssignments: any[];
  upcomingExams: any[];
  overdueInvoices: any[];
  latestStars: any | null;
  weeklyData: any[];
  todayActivities: number;
  childMood: { sentiment: string; notes: string | null } | null;
  weeklyComparison: { current: number; previous: number } | null;
}

const sentimentEmoji: Record<string, string> = {
  positive: "😊",
  neutral: "😐",
  negative: "😔",
};

const sentimentLabel: Record<string, string> = {
  positive: "سعيد",
  neutral: "عادي",
  negative: "حزين",
};

export default function ParentDailySummary({
  children,
  todayAttendance,
  todayLessonsCount,
  pendingAssignments,
  upcomingExams,
  overdueInvoices,
  latestStars,
  weeklyData,
  todayActivities,
  childMood,
  weeklyComparison,
}: Props) {
  return (
    <Card className="shadow-card border-r-4 border-r-primary bg-gradient-to-l from-primary/5 to-transparent">
      <CardContent className="p-4 space-y-3">
        <h3 className="font-bold text-foreground flex items-center gap-2">📋 ملخص اليوم</h3>
        
        {/* Attendance badges */}
        <div className="flex flex-wrap gap-3">
          {children.map((child) => {
            const attended = todayAttendance.some((a) => a.attendee_id === child.id);
            return (
              <Badge
                key={child.id}
                variant={attended ? "default" : "secondary"}
                className={`text-xs px-3 py-1.5 gap-1.5 ${attended ? "bg-emerald-500 hover:bg-emerald-600 text-white" : ""}`}
              >
                {attended ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
                {child.full_name} {attended ? "حضر ✔️" : "لم يسجل حضور"}
              </Badge>
            );
          })}
        </div>

        {/* Quick stats row */}
        <div className="flex flex-wrap gap-4 text-sm">
          {todayLessonsCount > 0 && (
            <div className="flex items-center gap-1.5 text-primary">
              <span className="text-lg">📚</span>
              <span className="font-medium">{todayLessonsCount} درس جديد اليوم</span>
            </div>
          )}
          {pendingAssignments.length > 0 && (
            <div className="flex items-center gap-1.5 text-amber-600">
              <span className="text-lg">📒</span>
              <span className="font-medium">{pendingAssignments.length} واجب ينتظر التسليم</span>
            </div>
          )}
          {upcomingExams.length > 0 && (
            <div className="flex items-center gap-1.5 text-primary">
              <span className="text-lg">📝</span>
              <span className="font-medium">
                {upcomingExams.length === 1 ? `امتحان قادم: ${upcomingExams[0].title}` : `${upcomingExams.length} امتحانات قادمة`}
              </span>
            </div>
          )}
          {todayActivities > 0 && (
            <div className="flex items-center gap-1.5 text-purple-600">
              <Activity className="w-4 h-4" />
              <span className="font-medium">{todayActivities} نشاط تكميلي اليوم</span>
            </div>
          )}
          {pendingAssignments.length === 0 && upcomingExams.length === 0 && todayLessonsCount === 0 && (
            <div className="flex items-center gap-1.5 text-emerald-600">
              <span className="text-lg">🎉</span>
              <span className="font-medium">لا توجد مهام معلقة - يوم رائع!</span>
            </div>
          )}
        </div>

        {/* Child mood */}
        {childMood && (
          <div className="bg-muted/50 rounded-lg p-3 flex items-center gap-3 border border-border">
            <span className="text-2xl">{sentimentEmoji[childMood.sentiment] || "😐"}</span>
            <div>
              <p className="font-semibold text-foreground text-sm">
                حالة الطفل: {sentimentLabel[childMood.sentiment] || "عادي"}
              </p>
              {childMood.notes && (
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{childMood.notes}</p>
              )}
            </div>
          </div>
        )}

        {/* Weekly comparison */}
        {weeklyComparison && (
          <div className="bg-muted/30 rounded-lg p-3 flex items-center gap-3 border border-border">
            <span className="text-2xl">{weeklyComparison.current >= weeklyComparison.previous ? "📈" : "📉"}</span>
            <div>
              <p className="font-semibold text-foreground text-sm">
                تقدم أسبوعي: {weeklyComparison.current}%
              </p>
              <div className="flex items-center gap-2 mt-1">
                <Progress value={weeklyComparison.current} className="h-2 flex-1" />
                <span className={`text-xs font-medium ${weeklyComparison.current >= weeklyComparison.previous ? "text-emerald-600" : "text-destructive"}`}>
                  {weeklyComparison.current >= weeklyComparison.previous ? "+" : ""}{weeklyComparison.current - weeklyComparison.previous}% عن الأسبوع الماضي
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Overdue invoices */}
        {overdueInvoices.length > 0 && (
          <div className="bg-destructive/10 rounded-lg p-3 flex items-center gap-3 border border-destructive/20">
            <Receipt className="w-5 h-5 text-destructive shrink-0" />
            <div>
              <p className="font-semibold text-destructive text-sm">
                {overdueInvoices.length} فاتورة متأخرة السداد
              </p>
              <p className="text-xs text-destructive/80">
                إجمالي: {overdueInvoices.reduce((sum: number, inv: any) => sum + (inv.amount || 0), 0)} ر.س
              </p>
            </div>
          </div>
        )}

        {latestStars && (
          <div className="bg-amber-50 dark:bg-amber-950/30 rounded-lg p-3 flex items-center gap-3 border border-amber-200 dark:border-amber-800">
            <span className="text-2xl">🌟</span>
            <div>
              <p className="font-semibold text-amber-700 dark:text-amber-300 text-sm">
                طفلك حصل على {latestStars.stars} نجوم في {latestStars.game_type}!
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-400">أداء ممتاز! استمر في التشجيع 💪</p>
            </div>
          </div>
        )}

        {weeklyData.some((d) => d.score !== null) && (
          <div className="mt-2">
            <p className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1.5">📊 تطور المستوى خلال الأسبوع</p>
            <div className="h-[120px] w-full" dir="ltr">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={weeklyData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="weekGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 9 }} stroke="hsl(var(--muted-foreground))" />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 8,
                      fontSize: 11,
                      direction: "rtl",
                    }}
                    formatter={(value: any) => [value !== null ? `${value}%` : "—", "المعدل"]}
                  />
                  <Area type="monotone" dataKey="score" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#weekGrad)" connectNulls dot={{ r: 3, fill: "hsl(var(--primary))" }} activeDot={{ r: 5 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
